import { useState } from 'react';
import { Button, Card, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  PAYMENT_PAGE_SIZE,
  itemsOf,
  listPayments,
  type PAList,
  type PAPayment,
} from '../api/money';
import { formatInstantDay } from '../format';
import { Money } from '../money/Money';
import { DataTable, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { Pager } from '../money/Pager';
import { usePAQuery } from '../money/usePAQuery';
import {
  correctionLabel,
  paymentSourceLabel,
  paymentStateLabel,
  refundStateLabel,
} from '../money/labels';

/**
 * «История платежей» — Appendix L §22, and the screen that has to make an APPEND-ONLY LEDGER
 * readable.
 *
 * ============================================================================
 * THE CORRECTIONS ARE ROWS IN THIS LIST. There is no separate corrections table.
 * ============================================================================
 *
 * §2.5's last paragraph: a Staff cancellation, an amount correction, a transfer to another
 * Contract and an acquirer refund are all «visible in the payment history through the same
 * cancellation/correction records». So a cancelled payment of 250 appears TWICE — the
 * original, now `state = cancelled`, and a REVERSAL row of −250 — and both must be legible as
 * what they are. Three fields do that work and all three come from the server:
 *
 *   `state`           «Принят» / «Аннулирован» — the original's own standing;
 *   `direction`       credit / reversal / debit. Without it a −250 reversal is
 *                     indistinguishable from a payment of minus two hundred and fifty;
 *   `correction_kind` WHICH of the four corrections produced the row, so the reversal reads
 *                     «сторнирование: платёж аннулирован» rather than as a mystery.
 *
 * ⚠ **AND NOTHING ON THIS SCREEN IS SUMMED.** There is no «итого» line, deliberately: the
 * whole point of the pair above is that the two rows cancel, and a client-side total would be
 * a fourth implementation of a sum this system already publishes twice (the contract balance
 * on the dashboard, `payments_received` on «Состояние счёта»). A screen that added up the
 * visible page would also produce a different answer from the balance the moment a date filter
 * excluded one half of a correction pair — which is precisely the shape the server's own
 * account-state loop got wrong once, reporting a cancelled 250 as MINUS 250. The balance is
 * one screen away and it is authoritative.
 *
 * `auto_charge` is shown because a subscriber who pressed nothing is entitled to know which
 * payments the system took from his saved card on its own initiative.
 *
 * ⚠ THE PAGER COUNTS ROWS AND NOT MONEY, and the distinction is the reason it is allowed on
 * a screen that forbids a total. `total` is the server's own count over the filtered set;
 * the list is capped at the server's page and the caller supplies only `?offset=`. Before
 * 2026-08-12 neither was used, so a subscriber with 812 payments was shown the newest 500
 * with nothing on the page saying that 312 existed.
 */
export function PaymentsScreen() {
  return (
    <MoneyPage titleKey="payments.title" leadKey="payments.lead">
      {(contract) => <PaymentsBody contractID={contract.id} />}
    </MoneyPage>
  );
}

function PaymentsBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  // The typed values, and the APPLIED ones. §22's filter has a «Показать» button, so a
  // half-typed date never triggers a request — and a YYYY-MM-DD the server cannot parse is a
  // 400 rather than a silent default (`pa.invalid_date`).
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  // ⚠ THE ENVELOPE IS KEPT WHOLE, `total` AND ALL. `itemsOf` used to be applied here and
  // the count thrown away, so the server's own answer to «how many are there?» never
  // reached the screen: a subscriber with 812 payments saw the newest 500 and was told
  // nothing. The offset resets on a filter change, because page 2 of the old filter is
  // not page 2 of the new one.
  const [offset, setOffset] = useState(0);

  const { query, reload } = usePAQuery<PAList<PAPayment>>(
    () => listPayments(contractID, { from: range.from, to: range.to, offset }),
    [contractID, range.from, range.to, offset],
  );

  const columns: ReadonlyArray<Column<PAPayment>> = [
    {
      key: 'date',
      labelKey: 'payments.columnDate',
      render: (p) => formatInstantDay(p.received_at),
    },
    {
      key: 'number',
      labelKey: 'payments.columnNumber',
      track: 'minmax(0, 1.6fr)',
      render: (p) => <span className="mono">{p.number}</span>,
    },
    {
      key: 'source',
      labelKey: 'payments.columnSource',
      render: (p) => (
        <>
          {/* THE DIRECTION IS PART OF THE METHOD. A funds transfer writes the same
              `source` on both halves; without the direction the DEBIT — money leaving
              this contract — read «Перевод с другого договора». */}
          {paymentSourceLabel(p.source, p.direction, t)}
          {p.auto_charge ? <span className="pa-cell-sub">{t('payments.autoCharge')}</span> : null}
        </>
      ),
    },
    {
      key: 'amount',
      labelKey: 'payments.columnAmount',
      className: 'pa-td-right',
      render: (p) => <Money minor={p.amount} currency={p.currency} colored />,
    },
    {
      key: 'state',
      labelKey: 'payments.columnState',
      render: (p) => (
        <span className={p.state === 'cancelled' ? 'pa-status pa-status-bad' : 'pa-status'}>
          {paymentStateLabel(p.state, t)}
        </span>
      ),
    },
  ];

  return (
    <>
      <Card className="pa-block pa-filters">
        <div className="pa-picker">
          <label className="pa-picker-label" htmlFor="pa-pay-from">
            {t('payments.fromLabel')}
          </label>
          <Input
            id="pa-pay-from"
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
          />
        </div>
        <div className="pa-picker">
          <label className="pa-picker-label" htmlFor="pa-pay-to">
            {t('payments.toLabel')}
          </label>
          <Input
            id="pa-pay-to"
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
          />
        </div>
        <Button
          type="primary"
          onClick={() => {
            setOffset(0);
            setRange({ from: fromInput, to: toInput });
          }}
        >
          {t('payments.apply')}
        </Button>
      </Card>

      <QueryFrame query={query} reload={reload}>
        {(list) => (
          <>
            <DataTable
              columns={columns}
              rows={itemsOf(list)}
              rowKey={(p) => p.id}
              emptyKey="payments.empty"
              rowClass={(p) => (p.direction === 'reversal' ? 'pa-row-reversal' : '')}
              detail={(p) => <PaymentDetail payment={p} />}
            />
            <Pager
              offset={offset}
              pageSize={PAYMENT_PAGE_SIZE}
              shown={itemsOf(list).length}
              total={list.total}
              onOffset={setOffset}
            />
          </>
        )}
      </QueryFrame>
    </>
  );
}

/**
 * The line under a payment that explains a correction.
 *
 * It renders NOTHING for an ordinary payment — most rows — so the list stays a list. For a
 * correction it names what the row is and, on a reversal, where the money to the card has got
 * to: §2.5 says an acquirer refund takes 3–10 banking days and is visible «through the same
 * cancellation/correction records», and `acquirer_refund_state` is the field that says so.
 */
function PaymentDetail({ payment }: { payment: PAPayment }) {
  const { t } = useTranslation();
  const correction = correctionLabel(payment, t);
  const refund = refundStateLabel(payment.acquirer_refund_state, t);
  const cancelled = payment.state === 'cancelled' && payment.cancelled_at;

  if (!correction && !refund && !cancelled) return null;

  return (
    <div className="pa-row-note">
      {correction ? <span>{correction}</span> : null}
      {cancelled ? (
        <span>
          {t('payments.cancelledAt', { when: formatInstantDay(payment.cancelled_at) })}
        </span>
      ) : null}
      {refund ? <span>{refund}</span> : null}
    </div>
  );
}
